import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Save, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { getRamoById, createRamo, updateRamo } from "../../services/adminRamosService";

export default function RamosForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    nombre: "",
    codigo: "",
    descripcion: ""
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) loadRamo();
  }, [id]);

  const loadRamo = async () => {
    try {
      setLoading(true);
      const data = await getRamoById(id);
      setFormData({
        nombre: data?.nombre || "",
        codigo: data?.codigo || "",
        descripcion: data?.descripcion || ""
      });
    } catch (error) {
      toast.error('Error al cargar el ramo');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    try {
      setSaving(true);
      if (isEditing) {
        await updateRamo(id, formData);
        toast.success('Ramo actualizado exitosamente');
      } else {
        await createRamo(formData);
        toast.success('Ramo creado exitosamente');
        navigate('/admin/ramos');
      }
    } catch (error) {
      toast.error('Error al guardar el ramo');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Editar Ramo' : 'Nuevo Ramo'}
        </h1>
        <p className="text-gray-600">
          {isEditing ? 'Modifica los detalles del ramo' : 'Crea un nuevo ramo'}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Información del Ramo</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Nombre *</Label>
                <Input name="nombre" value={formData.nombre} onChange={handleChange} required />
              </div>
              <div>
                <Label>Código</Label>
                <Input name="codigo" value={formData.codigo} onChange={handleChange} />
              </div>
              <div>
                <Label>Descripción</Label>
                <Input name="descripcion" value={formData.descripcion} onChange={handleChange} />
              </div>
            </div>

            <div className="flex justify-between pt-6">
              <Button type="button" variant="outline" onClick={() => navigate('/admin/ramos')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
              <Button type="submit" disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}